// Libs
import classNames from 'classnames/bind';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseInput, FileUpload, Loading } from '~/components/common';
import {
  EstimateTemplateDiscountsComponent,
  EstimateTemplateMaterialsComponent,
  EstimateTemplateServiceComponent,
} from '~/components/module/estimateTemplate';
// Others
import {
  IEstimateTemplateDetails,
  IFormEstimateTemplate,
  IUpdateTemplateBody,
} from '~/utils/interface/estimateTemplate';
import {
  defaultValuesDiscount,
  defaultValuesForm,
  defaultValuesMaterial,
  defaultValuesService,
  schema,
} from './helper';
import {
  BACK_TO_PREVIOUS_PAGE,
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE,
} from '~/utils/constants/common';
import { IFileUpload } from '~/utils/interface/common';
import { compareDataUpdate, createFormData, formatCurrency, removeEmptyObjects } from '~/utils/helper/common';
import { TypeDiscountEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  createEstimateTemplate,
  getEstimateTemplateDetails,
  updateEstimateTemplate,
} from '~/thunks/estimateTemplate/estimateTemplateThunk';
import { getServiceList } from '~/thunks/service/serviceThunk';
import { IQueryParamService, ISelectService } from '~/utils/interface/service';
import { IQueryParamMaterial, ISelectMaterial } from '~/utils/interface/material';
import { getMaterialList } from '~/thunks/material/materialThunk';
// Styles, images, icons
import styles from './FormEstimateTemplate.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const FormEstimateTemplate = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimateTemplate']);
  const { id: estimateTemplateId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const methods = useForm<IFormEstimateTemplate>({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      ...defaultValuesForm,
      ...(estimateTemplateId ? {} : { estimateServices: [defaultValuesService] }),
      ...(estimateTemplateId ? {} : { estimateMaterials: [defaultValuesMaterial] }),
      ...(estimateTemplateId ? {} : { discounts: [defaultValuesDiscount] }),
    },
  });
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = methods;

  const {
    fields: serviceFields,
    append: serviceAppend,
    remove: serviceRemove,
  } = useFieldArray({ control, name: 'estimateServices' });

  const {
    fields: materialFields,
    append: materialAppend,
    remove: materialRemove,
  } = useFieldArray({ control, name: 'estimateMaterials' });

  const {
    fields: discountFields,
    append: discountAppend,
    remove: discountRemove,
  } = useFieldArray({ control, name: 'discounts' });

  const service = useWatch({ control, name: 'estimateServices', defaultValue: [] });
  const material = useWatch({ control, name: 'estimateMaterials', defaultValue: [] });
  const discount = useWatch({ control, name: 'discounts', defaultValue: [] });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormEstimateTemplate>(defaultValuesForm);

  const [optionService, setOptionService] = useState<ISelectService[]>([]);
  const [isLoadingService, setIsLoadingService] = useState<boolean>(false);

  const [optionMaterials, setOptionMaterials] = useState<ISelectMaterial[]>([]);
  const [isLoadingMaterial, setIsLoadingMaterial] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  const subtotal = useMemo(() => {
    const total = [...(service || []), ...(material || [])].reduce((sum, item) => {
      const price = Number(item.price) || DEFAULT_NUMBER_ZERO;
      const quantity = Number(item.quantity) || DEFAULT_NUMBER_ZERO;

      return sum + price * quantity;
    }, DEFAULT_NUMBER_ZERO);

    return total;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, material]);

  const totalDiscount = useMemo(() => {
    if (!discount || discount.length === DEFAULT_NUMBER_ZERO) return DEFAULT_NUMBER_ZERO;

    const { totalPercentage, totalDollar } = discount.reduce(
      (acc, item) => {
        const rate = Number(item.rate) || DEFAULT_NUMBER_ZERO;
        if (item.type === TypeDiscountEnum.PERCENTAGE) {
          acc.totalPercentage += rate;
        } else if (item.type === TypeDiscountEnum.DOLLAR) {
          acc.totalDollar += rate;
        }

        return acc;
      },
      { totalPercentage: DEFAULT_NUMBER_ZERO, totalDollar: DEFAULT_NUMBER_ZERO }
    );

    return (subtotal * totalPercentage) / 100 + totalDollar;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, discount]);

  const total = useMemo(() => {
    return Math.max(subtotal - totalDiscount, DEFAULT_NUMBER_ZERO);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, totalDiscount]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setValue('total', String(total), { shouldValidate: true });
  }, [total, setValue]);
  useEffect(() => {
    if (!estimateTemplateId) return;

    handleGetEstimateTemplateDetails(estimateTemplateId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateTemplateId]);

  useEffect(() => {
    handleGetServiceList();
    handleGetMaterialsList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetServiceList = () => {
    const params: IQueryParamService = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
    };

    setIsLoadingService(true);

    dispatch(getServiceList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        if (!responses) return;

        const dataService: ISelectService[] = responses.map((item) => ({
          value: item?.id,
          label: item?.name || EMPTY_STRING,
          price: item?.price || EMPTY_STRING,
          unit: item?.unit || EMPTY_STRING,
          // quantity: item?.quantity || EMPTY_STRING,
          description: item?.description || EMPTY_STRING,
        }));
        setOptionService(dataService);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingService(false);
      });
  };

  const handleGetMaterialsList = () => {
    const params: IQueryParamMaterial = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
    };

    setIsLoadingMaterial(true);

    dispatch(getMaterialList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;

        if (!responses) return;

        const dataMaterials: ISelectMaterial[] = responses.map((item) => ({
          value: item?.id,
          label: item?.name || EMPTY_STRING,
          price: item?.price || EMPTY_STRING,
          unit: item?.unit || EMPTY_STRING,
          quantity: item?.quantity || EMPTY_STRING,
          description: item?.description || EMPTY_STRING,
        }));
        setOptionMaterials(dataMaterials);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingMaterial(false);
      });
  };

  const handleCancelFormEstimateTemplate = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };

  const handleGetEstimateTemplateDetails = (templateId: string) => {
    if (!templateId) return;

    setIsLoading(true);

    dispatch(getEstimateTemplateDetails(templateId))
      .unwrap()
      .then((res) => {
        const templateDetails: IEstimateTemplateDetails = res?.data;

        const newData: IFormEstimateTemplate = {
          templateName: templateDetails?.templateName || EMPTY_STRING,
          optionName: templateDetails?.optionName || EMPTY_STRING,
          optionDescription: templateDetails?.optionDescription || EMPTY_STRING,
          templateEstimateImage: {
            url: templateDetails?.templateEstimateImage || EMPTY_STRING,
          },
          estimateServices: templateDetails?.services || [],
          estimateMaterials: templateDetails?.materials || [],
          discounts: templateDetails?.discounts || [],
          total: String(templateDetails?.total) ?? EMPTY_STRING,
        };

        const dataConvert: IFormEstimateTemplate = removeEmptyObjects(newData) as IFormEstimateTemplate;

        setInitialData(dataConvert);
        reset(dataConvert);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdateEstimateTemplate = async (data: IFormEstimateTemplate) => {
    if (!estimateTemplateId) return;

    const dataChange = compareDataUpdate(initialData, data) as IFormEstimateTemplate;
    const { templateEstimateImage, ...newData } = dataChange;

    const dataConvert: IFormEstimateTemplate = {
      ...newData,
      ...(data.templateEstimateImage?.file
        ? { templateEstimateImage: data.templateEstimateImage }
        : { deleteImage: true }),
      estimateServices: data.estimateServices || [],
      estimateMaterials: data.estimateMaterials || [],
      discounts: data.discounts || [],
    };

    const formDataEstimateTemplate = await createFormData(dataConvert, 'templateEstimateImage');

    const payload: IUpdateTemplateBody = {
      templateId: estimateTemplateId,
      formData: formDataEstimateTemplate,
    };

    setIsLoading(true);

    dispatch(updateEstimateTemplate(payload))
      .unwrap()
      .then((res) => {
        handleCancelFormEstimateTemplate();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCreateEstimateTemplate = async (data: IFormEstimateTemplate) => {
    if (!data) return;

    setIsLoading(true);

    const dataConvert = removeEmptyObjects(data);

    const formDataEstimateTemplate = await createFormData(dataConvert, 'templateEstimateImage');

    dispatch(createEstimateTemplate(formDataEstimateTemplate))
      .unwrap()
      .then(() => {
        handleCancelFormEstimateTemplate();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitFormEstimateTemplate = async (data: IFormEstimateTemplate) => {
    const newData: IFormEstimateTemplate = {
      ...data,
      estimateServices: data?.estimateServices?.map((service) => {
        const { name, ...newService } = service;

        return newService;
      }),
      estimateMaterials: data?.estimateMaterials?.map((material) => {
        const { name, ...newMaterial } = material;

        return newMaterial;
      }),
    };

    if (estimateTemplateId) {
      handleUpdateEstimateTemplate(newData);
    } else {
      handleCreateEstimateTemplate(newData);
    }
  };

  const handleAddItemService = () => {
    serviceAppend(defaultValuesService);
  };

  const handleRemoveServiceField = (index: number) => {
    serviceRemove(index);
  };

  const handleAddItemMaterial = () => {
    materialAppend(defaultValuesMaterial);
  };

  const handleRemoveMaterialField = (index: number) => {
    materialRemove(index);
  };

  const handleAddItemDiscount = () => {
    discountAppend(defaultValuesDiscount);
  };

  const handleRemoveDiscountField = (index: number) => {
    discountRemove(index);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formEstimateTemplatePage'
      title={estimateTemplateId ? t('form_estimate_template_btn_edit') : t('form_estimate_template_btn_add')}
      onBackToPreviousPage={handleCancelFormEstimateTemplate}
    >
      <>
        <FormProvider {...methods}>
          <Stack
            component={'form'}
            spacing={'20px'}
            onSubmit={handleSubmit(handleSubmitFormEstimateTemplate)}
            className={cx('form')}
          >
            <Box component={'div'} className={cx('formBody')}>
              <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
                <Grid2 size={12}>
                  <Controller
                    name='templateName'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <BaseInput
                        required
                        label={t('form_estimate_template_label_template_name')}
                        value={value || EMPTY_STRING}
                        onChange={onChange}
                        errorMessage={errors?.templateName?.message}
                      />
                    )}
                  />
                </Grid2>

                <Grid2 container size={12} rowSpacing={'12px'}>
                  <Grid2 size={12}>
                    <Typography color='primary' className={cx('groupTitle')}>
                      {t('form_estimate_template_group_title_option_details')}
                    </Typography>
                  </Grid2>

                  <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                    <Grid2 size={12}>
                      <Controller
                        name='templateEstimateImage'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <FileUpload
                            accept={FILE_UPLOAD_DEFAULT_ACCEPT_IMAGE}
                            label={t('form_estimate_template_label_image')}
                            {...(estimateTemplateId && !value?.file && { uploadedFiles: value?.url })}
                            onChange={(files: IFileUpload[]) => onChange(files?.[DEFAULT_NUMBER_ZERO])}
                            errorMessage={errors.templateEstimateImage?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={12}>
                      <Controller
                        name='optionName'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInput
                            required
                            label={t('form_estimate_template_label_name')}
                            value={value || EMPTY_STRING}
                            onChange={onChange}
                            errorMessage={errors?.optionName?.message}
                          />
                        )}
                      />
                    </Grid2>

                    <Grid2 size={12}>
                      <Controller
                        name='optionDescription'
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <BaseInput
                            multiline
                            height={'auto'}
                            rows={6}
                            label={t('form_estimate_template_label_description')}
                            value={value || EMPTY_STRING}
                            onChange={onChange}
                            errorMessage={errors?.optionDescription?.message}
                          />
                        )}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Grid2 container size={12} rowSpacing={'12px'}>
                  <Grid2 size={12}>
                    <Typography color='primary' className={cx('groupTitle')}>
                      {t('form_estimate_template_group_title_services')}
                    </Typography>
                  </Grid2>

                  <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                    {serviceFields?.map((item, index) => (
                      <EstimateTemplateServiceComponent
                        index={index}
                        key={item.id}
                        options={optionService}
                        isLoading={isLoadingService}
                        onRemoveItem={handleRemoveServiceField}
                      />
                    ))}

                    <Grid2 size={12}>
                      <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={'4px'}
                        className={cx('addFieldGroup')}
                      >
                        <img
                          src={icons.commonIconAddField}
                          alt={t('altImg', { ns: 'common/base' })}
                          className={cx('addFieldIcon')}
                        />

                        <Typography color='primary' onClick={handleAddItemService} className={cx('addField')}>
                          {t('common_action_add_field', { ns: 'common/base' })}
                        </Typography>
                      </Stack>
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Grid2 container size={12} rowSpacing={'12px'}>
                  <Grid2 size={12}>
                    <Typography color='primary' className={cx('groupTitle')}>
                      {t('form_estimate_template_group_title_materials')}
                    </Typography>
                  </Grid2>

                  <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                    {materialFields?.map((item, index) => (
                      <EstimateTemplateMaterialsComponent
                        index={index}
                        key={item.id}
                        options={optionMaterials}
                        isLoading={isLoadingMaterial}
                        onRemoveItem={handleRemoveMaterialField}
                      />
                    ))}

                    <Grid2 size={12}>
                      <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={'4px'}
                        className={cx('addFieldGroup')}
                      >
                        <img
                          src={icons.commonIconAddField}
                          alt={t('altImg', { ns: 'common/base' })}
                          className={cx('addFieldIcon')}
                        />

                        <Typography
                          color='primary'
                          onClick={handleAddItemMaterial}
                          className={cx('addField')}
                        >
                          {t('common_action_add_field', { ns: 'common/base' })}
                        </Typography>
                      </Stack>
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Grid2 container size={12} rowSpacing={'12px'}>
                  <Grid2 size={12}>
                    <Typography color='primary' className={cx('groupTitle')}>
                      {t('form_estimate_template_group_title_discounts')}
                    </Typography>
                  </Grid2>

                  <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
                    {discountFields?.map((item, index) => (
                      <EstimateTemplateDiscountsComponent
                        index={index}
                        key={item.id}
                        onRemoveItem={handleRemoveDiscountField}
                      />
                    ))}

                    <Grid2 size={12}>
                      <Stack
                        direction={'row'}
                        alignItems={'center'}
                        spacing={'4px'}
                        className={cx('addFieldGroup')}
                      >
                        <img
                          src={icons.commonIconAddField}
                          alt={t('altImg', { ns: 'common/base' })}
                          className={cx('addFieldIcon')}
                        />

                        <Typography
                          color='primary'
                          onClick={handleAddItemDiscount}
                          className={cx('addField')}
                        >
                          {t('common_action_add_field', { ns: 'common/base' })}
                        </Typography>
                      </Stack>
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Stack direction='row' className={cx('viewTotal')}>
                  <Box className={cx('item')}>
                    <Box className={cx('textTitle')}>{t('form_estimate_template_label_subtotal')}</Box>
                    <Box className={cx('textSubtotal')}>{formatCurrency(subtotal)}</Box>
                  </Box>

                  <Box className={cx('item')}>
                    <Box className={cx('textTitle')}>{t('form_estimate_template_label_discount')}</Box>
                    <Box className={cx('textDiscount')}>{`-${formatCurrency(totalDiscount)}`}</Box>
                  </Box>

                  <Box className={cx('item')}>
                    <Box className={cx('textTitleTotal')}>{t('form_estimate_template_label_total')}</Box>
                    <Box className={cx('textSubtotal')}>{formatCurrency(total)}</Box>
                  </Box>
                </Stack>
              </Grid2>
            </Box>

            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'flex-end'}
              component={'div'}
              spacing={'12px'}
              className={cx('formFooter')}
            >
              <Button variant='outlined' onClick={handleCancelFormEstimateTemplate}>
                {t('common_action_cancel', { ns: 'common/base' })}
              </Button>

              <Button variant='contained' type='submit' disabled={estimateTemplateId ? !isDirty : false}>
                {t('common_action_save', { ns: 'common/base' })}
              </Button>
            </Stack>
          </Stack>
        </FormProvider>

        {isLoading && <Loading />}
      </>
    </ChildrenPage>
  );
};

export default FormEstimateTemplate;
